import { z } from 'zod';

import { getPassportDocSchema } from '@/pages/UserAccount/Profile';

import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';

import {
  zodCheckboxOnlyTrue,
  zodDate,
  zodEmail,
  zodMobilePhoneUA,
  zodSelectOption,
  zodStringOptional,
  zodStringRequiredWithMinMax,
} from '@/shared/lib/validations';
import { ukrainianAddressSchema } from '@/shared/ui/AddressBlock/model/schemas';

import { FormConstants } from '@/components/Forms/constants';

import { InsuranceOrganizationProductSchema } from '@/modules/bia/shared/model/schemas';

import { LELAL_AGE_DATE } from '@/helpers/dates';

import { DocumentTypeCode } from '@/api/types';

import { getOneDayFromToday, getEightDaysFromToday } from '../lib/constants';

export const insuranceSelectionSchema = z.object({
  insuranceProduct: InsuranceOrganizationProductSchema.nullable(),
});

export const insuredDataSchema = z.object({
  lastName: zodStringRequiredWithMinMax(),
  firstName: zodStringRequiredWithMinMax(),
  middleName: zodStringOptional,
  birthday: zodDate({ max: LELAL_AGE_DATE }),
  rnokpp: zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_10 }),
  email: zodEmail,
  phone: zodMobilePhoneUA,
});

export const insuredAddressSchema = z.object({
  insuredAddress: ukrainianAddressSchema,
});

const insuredDocumentBaseSchema = z.object({
  documents: z.any(),
  insuredDocument: zodSelectOption.extend({
    code: z.nativeEnum(DocumentTypeCode),
  }),
});

export const insuredDocumentSchema = insuredDocumentBaseSchema.superRefine((data, ctx) => {
  const documentType = data.insuredDocument.code;
  const schemaByDocumentTypeCode = getPassportDocSchema(documentType);
  const result = schemaByDocumentTypeCode?.safeParse(data.documents[documentType]);

  if (!result?.success) {
    result?.error.issues.forEach((issue) => {
      ctx.addIssue({
        ...issue,
        path: ['documents', documentType, ...issue.path],
      });
    });
  }
});

export const dataPreviewSchema = confirmationOfApplicationSchema.merge(
  z.object({
    insuranceStartDate: zodDate({
      min: () => getOneDayFromToday().toDate(),
      max: () => getEightDaysFromToday().toDate(),
    }),
  }),
);

export const insuranceServiceMergedSchema = insuranceSelectionSchema
  .merge(insuredDataSchema)
  .merge(insuredAddressSchema)
  .merge(dataPreviewSchema)
  .merge(insuredDocumentBaseSchema);

export const insuranceOrderPreviewModalSchema = z.object({
  confirmOffer: zodCheckboxOnlyTrue,
});
